import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Users, MapPin, Music, ArrowRight } from 'lucide-react';
import Logo from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/utils/translations';
import MaintenanceBanner from '@/components/MaintenanceBanner';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Maintenance Mode Banner */}
      <MaintenanceBanner />
      
      {/* Navigation */}
      <nav className="bg-neutral-900 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <Logo size="default" />
            </div>
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                className="text-white hover:text-[#f4911c] hover:bg-neutral-800"
                onClick={() => navigate('/login')}
                data-testid="nav-login-button"
              >
                {t('nav.login')}
              </Button>
              <Button 
                className="bg-[#f4911c] hover:bg-[#C41A20] text-white font-semibold"
                onClick={() => navigate('/register')}
                data-testid="nav-register-button"
              >
                {t('nav.register')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-neutral-900 via-neutral-800 to-neutral-900 text-white py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-5xl md:text-7xl font-bold mb-6 animate-fadeIn">
              {t('landing.title')}
              <span className="block text-[#f4911c] mt-2">{t('landing.subtitle')}</span>
            </h2>
            <p className="text-xl md:text-2xl text-neutral-300 mb-8 max-w-3xl mx-auto">
              {t('landing.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-[#f4911c] hover:bg-[#C41A20] text-white text-lg px-8 py-6 font-semibold"
                onClick={() => navigate('/register')}
                data-testid="hero-register-button"
              >
                {t('landing.applyNow')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-neutral-900 text-lg px-8 py-6 font-semibold"
                onClick={() => navigate('/login')}
                data-testid="hero-login-button"
              >
                {t('landing.alreadyMember')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-center text-neutral-900 mb-12">
            {t('landing.whyJoin')}
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-8 shadow-lg card-hover" data-testid="feature-card-experience">
              <div className="w-16 h-16 bg-[#f4911c] bg-opacity-10 rounded-full flex items-center justify-center mb-6">
                <Music className="w-8 h-8 text-[#f4911c]" />
              </div>
              <h4 className="text-2xl font-bold text-neutral-900 mb-4">{t('landing.festivalExperience')}</h4>
              <p className="text-neutral-600 leading-relaxed">
                {t('landing.festivalExperienceDesc')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg card-hover" data-testid="feature-card-community">
              <div className="w-16 h-16 bg-[#f4911c] bg-opacity-10 rounded-full flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-[#f4911c]" />
              </div>
              <h4 className="text-2xl font-bold text-neutral-900 mb-4">{t('landing.amazingCommunity')}</h4>
              <p className="text-neutral-600 leading-relaxed">
                {t('landing.amazingCommunityDesc')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-lg card-hover" data-testid="feature-card-flexible">
              <div className="w-16 h-16 bg-[#f4911c] bg-opacity-10 rounded-full flex items-center justify-center mb-6">
                <Calendar className="w-8 h-8 text-[#f4911c]" />
              </div>
              <h4 className="text-2xl font-bold text-neutral-900 mb-4">{t('landing.flexibleShifts')}</h4>
              <p className="text-neutral-600 leading-relaxed">
                {t('landing.flexibleShiftsDesc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-4xl font-bold text-center text-neutral-900 mb-12">
            {t('landing.howItWorks')}
          </h3>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#f4911c] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h5 className="text-xl font-semibold text-neutral-900 mb-2">{t('landing.step1')}</h5>
              <p className="text-neutral-600">{t('landing.step1Desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#f4911c] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h5 className="text-xl font-semibold text-neutral-900 mb-2">{t('landing.step2')}</h5>
              <p className="text-neutral-600">{t('landing.step2Desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#f4911c] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h5 className="text-xl font-semibold text-neutral-900 mb-2">{t('landing.step3')}</h5>
              <p className="text-neutral-600">{t('landing.step3Desc')}</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#f4911c] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                4
              </div>
              <h5 className="text-xl font-semibold text-neutral-900 mb-2">{t('landing.step4')}</h5>
              <p className="text-neutral-600">{t('landing.step4Desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-4xl md:text-5xl font-bold mb-6">
            {t('landing.readyToJoin')}
          </h3>
          <p className="text-xl text-neutral-300 mb-8">
            {t('landing.applicationsOpen')}
          </p>
          <Button 
            size="lg"
            className="bg-[#f4911c] hover:bg-[#C41A20] text-white text-lg px-8 py-6 font-semibold"
            onClick={() => navigate('/register')}
            data-testid="cta-apply-button"
          >
            {t('landing.applyToVolunteer')}
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-950 text-neutral-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Logo size="small" />
              </div>
              <p className="text-sm">
                Denmark's premier music festival under the bridge. Join our crew and experience the magic.
              </p>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.quickLinks')}</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://rockunderbroen.dk/om-rub/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f4911c]">{t('footer.about')}</a></li>
                <li><a href="https://rockunderbroen.dk/frivillig/" target="_blank" rel="noopener noreferrer" className="hover:text-[#f4911c]">{t('footer.volunteerFAQ')}</a></li>
                <li><a href="mailto:frivillig@rub.dk" className="hover:text-[#f4911c]">{t('footer.contact')}</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-white font-semibold mb-4">{t('footer.contactInfo')}</h4>
              <p className="text-sm mb-2">frivillig@rub.dk</p>
              <p className="text-sm">Middelfart, Denmark</p>
            </div>
          </div>
          
          <div className="border-t border-neutral-800 mt-8 pt-8">
            <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0 text-sm">
              <p className="text-center md:text-left">&copy; 2025 Rock Under Broen. {t('footer.rights')}.</p>
              <div className="flex items-center space-x-6">
                <a href="/privacy-policy" className="hover:text-[#f4911c] transition-colors">
                  Privatlivspolitik
                </a>
                <a href="mailto:frivillig@rub.dk" className="hover:text-[#f4911c] transition-colors">
                  Kontakt
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;